import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// import validator from 'validator';

import { translate } from '../../../../base/i18n';
import { bindMeetFormData, bindPaySecTouched, bindPaymentError, bindTouchedFields, bindValidationSection } from '../../../actions';

/**
 * payment section for clickandpledge
 * @param {Object} props - Component props
 * @param {Function} props.t - Translation function
 * @param {Object} props.paymentinfo - Payment information
 * @param {string} props.sectionid - Section ID
 * @param {string} props.sectionname - Section name
 * @param {Object} props.paymentValidator - Payment validator
 * @returns {JSX.Element} PaymentSection component
 */
function PaymentSection({
    t,
    paymentinfo,
    sectionid,
    sectionname,
    paymentValidator
}) {
    const [ cardType, setCardType ] = useState('');
    const [ expirationError, setExpirationError ] = useState(false);
    const [ cvvError, setCvvError ] = useState(false);

    const isPaySecTouched = useSelector(
        state => state['features/clickandpledge']?.isPaySecTouched || false
    );
    const [ isReadOnly, setIsReadOnly ] = useState(true);
    const [ isZipReadOnly, setZipIsReadOnly ] = useState(true);
    const [ month, setMonth ] = useState('');
    const [ year, setYear ] = useState('');

    const meetuserData = useSelector(state => state['features/clickandpledge']?.meetData || []);
    const formSettings = useSelector(
    state => state['features/clickandpledge']?.formSettings || {}
    );

    const meetformData = useSelector(
    state => state['features/clickandpledge']?.meetFormData || {}
    );

    const validationSec = useSelector(
    state => state['features/clickandpledge']?.validationSection || {}
    );
    const paymentFields = useSelector(
    state => state['features/clickandpledge']?.paymentSection || []
    );

    const fadingRef = useRef(null);
    const intervalRef = useRef(null);
    const firstName = useRef(null);
    const lastName = useRef(null);
    const noc = useRef(null);
    const ccn = useRef(null);
    const expMonth = useRef(null);
    const cvv = useRef(null);
    const zip = useRef(null);

    const touchedfields = useSelector(
    state => state['features/clickandpledge']?.touchedfields || []
    );
    const yearRef = useRef(null);

    /**
     * Get card type based on account number
     * @param {string} accountNumber - Credit card number
     */
    const getCardType = accountNumber => {

        // first check for MasterCard
        if (/^5[1-5]/.test(accountNumber)) {
            setCardType('CC_mastercard');
        } else if (/^4/.test(accountNumber)) {
            // then check for Visa
            setCardType('CC_visa');
        } else if (/^3[47]/.test(accountNumber)) {
            // then check for AmEx
            setCardType('CC_americanexpress');
        } else if (/^6011/.test(accountNumber)) {
            // then check for Discover
            setCardType('CC_discover');
        } else if (/^(?:2131|1800|35[0-9]{3})[0-9]{11,15}$/.test(accountNumber)) {
            // then check for jcb
            // else if (/^2131|1800/.test(accountNumber)) setCardType("CC_jcb");
            setCardType('CC_jcb');
        } else {
            setCardType('');
        }
    };

    const dispatch = useDispatch();

    const payValue = useMemo(() => {
        if (meetformData.formdata) {
            return meetformData.formdata.find(obj => obj[sectionid]);
        }

        return [];
    }, [ meetformData, sectionid ]);

    const rValue = payValue && payValue[sectionid];

    const cleanCcn = String(rValue?.ccn || '').replace(/\D/g, '');
    const isAmex = cardType === 'CC_americanexpress' || /^3[47]/.test(cleanCcn);
    const minCvvLen = isAmex ? 4 : 3;
    const isCvvInvalid = Boolean(rValue?.cvv && rValue.cvv.length < minCvvLen);

    /**
     * Function to change the list items
     * @param {NodeList} listItems - List of span elements
     * @param {number} listLen - Length of the list
     * @param {number} currentIndex - Current index
     * @returns {number} Next index
     */
    const changeList = (listItems, listLen, currentIndex) => {
        // Fade out the current item
        if (listLen) {
            listItems[currentIndex].style.opacity = 0;
            setTimeout(() => {
                // Hide the current item
                listItems[currentIndex].style.display = 'none';

                // Move to the next item
                const nextIndex = currentIndex + 1 === listLen ? 0 : currentIndex + 1;

                // Show and fade in the next item
                listItems[nextIndex].style.display = 'block';
                setTimeout(() => {
                    listItems[nextIndex].style.opacity = 1;
                }, 50); // Delay to ensure the element is fully displayed before fading in
            }, 300); // Fade out duration
        }

        return currentIndex + 1 === listLen ? 0 : currentIndex + 1;
    };

    useEffect(() => {

        const ItemList = Object.entries(paymentFields).filter(([ k, v ]) => v === true && k.startsWith('CC_'));

        // Get the list items and their length
        const container = fadingRef.current;

        if (container) {
            const listItems = container.querySelectorAll('span');

            // let listItems = document.querySelectorAll('.fading span');

            const listLen = ItemList.length;
            let i = 0;

            // Hide all items except the first one
            for (let j = 1; j < listLen; j++) {
                listItems[j].style.display = 'none';
            }

            // Call the function initially
            i = changeList(listItems, listLen, i);

            // Clear any existing intervals
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }

            // Set interval to change the list items periodically
            intervalRef.current = setInterval(() => {
                i = changeList(listItems, listLen, i);
            }, 3000); // Change every 3 seconds
        }


        // Cleanup function to clear the interval
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [ paymentFields ]);

    useEffect(() => {
        const cvvLabelLength = document.querySelector('.cvv_label label')?.textContent?.trim()?.length || 0;
        const zipLabelLength = document.querySelector('.zip_label label')?.textContent?.trim()?.length || 0;

        if (cvvLabelLength > 6 || zipLabelLength > 6) {
            // If labels are too long, we could implement alternative layout here
            // For now, just keep the inline layout as it's the only one remaining
            document.querySelector('.inline_card_info')?.classList.remove('d-none');
            document.querySelector('.inline-err-msg')?.classList.remove('d-none');
        } else {
            document.querySelector('.inline_card_info')?.classList.remove('d-none');
            document.querySelector('.inline-err-msg')?.classList.remove('d-none');
        }
    }, [ formSettings ]);

    useEffect(() => {
        if (touchedfields.length) {
            if (rValue && rValue.expiryMonth && touchedfields.includes('expiryMonth')) {
                const filtered = touchedfields.filter(fil => fil !== 'expiryMonth');

                dispatch(bindTouchedFields(filtered));
            }
            if (rValue && rValue.expiryYear && touchedfields.includes('expiryYear')) {
                const filtered = touchedfields.filter(fil => fil !== 'expiryYear');

                dispatch(bindTouchedFields(filtered));
            }
        }

        if (rValue && !rValue.expiryMonth && month) {
            setMonth('');
        }
        if (rValue && !rValue.expiryYear && year) {
            setYear('');
        }
    }, [ rValue?.expiryMonth, rValue?.expiryYear, month, year, touchedfields, dispatch ]);

    useEffect(() => {
        if (rValue && rValue.ccn) {
            const rawDigits = String(rValue.ccn).replace(/\D/g, '');

            getCardType(rawDigits);
            const isAmexCard = /^3[47]/.test(rawDigits);
            const minLen = isAmexCard ? 4 : 3;

            if (rValue.cvv) {
                if (rValue.cvv.length < minLen) {
                    setCvvError(true);
                    dispatch(bindPaymentError(true));
                } else {
                    setCvvError(false);
                    if (!expirationError) {
                        dispatch(bindPaymentError(false));
                    }
                }
            }
        }
    }, [ rValue?.ccn, rValue?.cvv ]);

    const onBlurChange = field => {
        let error = false;

        if (field === 'cvv' && rValue) {
            const rawDigits = String(rValue.ccn || '').replace(/\D/g, '');
            const isAmexCard = cardType === 'CC_americanexpress' || /^3[47]/.test(rawDigits);
            const minLen = isAmexCard ? 4 : 3;

            if (!rValue.cvv || rValue.cvv.length < minLen) {
                setCvvError(true);
                dispatch(bindPaymentError(true));
                error = true;
            } else {
                setCvvError(false);
                if (!expirationError) {
                    dispatch(bindPaymentError(false));
                    error = false;
                }
            }
        }
        if (field === 'ccn' && rValue && rValue.ccn) {
            const rawDigits = String(rValue.ccn || '').replace(/\D/g, '');
            const isAmexCard = cardType === 'CC_americanexpress' || /^3[47]/.test(rawDigits);
            const minLen = isAmexCard ? 4 : 3;

            if (rValue.cvv && rValue.cvv.length < minLen) {
                setCvvError(true);
                dispatch(bindPaymentError(true));
                error = true;
            } else if (rValue.cvv) {
                setCvvError(false);
                if (!expirationError && !error) {
                    dispatch(bindPaymentError(false));
                    error = false;
                }
            }
        }

        if (!touchedfields.includes(field)) {
            dispatch(bindTouchedFields([ ...touchedfields, field ]));
        }
        paymentValidator.current.showMessageFor(field);
        dispatch(
            bindValidationSection({ AmountSection: false,
                PaymentSection: true })
        );
        dispatch(bindPaySecTouched(true));
    };


    const handleFocus = () => {
        setIsReadOnly(false);
    };
    const handleZipFocus = () => {
        setZipIsReadOnly(false);
    };

    const checkCurrentDate = (monthValue, yearValue) => {
        let CurrMonth = new Date().getMonth() + 1;

        CurrMonth = CurrMonth.toString().length === 1 ? `0${CurrMonth}` : CurrMonth;
        const currentYear = new Date().getFullYear() % 100;

        if (yearValue && monthValue && parseInt(yearValue, 10) === parseInt(currentYear, 10) && parseInt(monthValue, 10) < parseInt(CurrMonth, 10)) {
            dispatch(
                bindMeetFormData({
                    formdata: meetformData.formdata
                        ? meetformData.formdata.map(q => {
                            return {
                                ...q,
                                [sectionid]: {
                                    ...q[sectionid],
                                    'expiryMonth': ''
                                }
                            };
                        })
                        : [ { [sectionid]: { 'expiryMonth': '' } } ]
                })
            );
            if (rValue && !rValue.expiryYear && touchedfields.includes('expiryYear')) {
                setExpirationError(true);
                dispatch(bindPaymentError(true));
            } else {
                setExpirationError(false);
                if (!cvvError) {
                    dispatch(bindPaymentError(false));
                }
            }

            if (!touchedfields.includes('expiryMonth')) {
                dispatch(bindTouchedFields([ ...touchedfields, 'expiryMonth' ]));
            }

            return false;
        }

        return true;

    };

    const handleMonthChange = e => {
        let value = e.target.value;

        // Allow empty value for backspace/delete
        if (value === '') {
            setMonth('');
            dispatch(
                bindMeetFormData({
                    formdata: meetformData.formdata
                        ? meetformData.formdata.map(q => {
                            return {
                                ...q,
                                [sectionid]: {
                                    ...q[sectionid],
                                    expiryMonth: ''
                                }
                            };
                        })
                        : [ { [sectionid]: { expiryMonth: '' } } ]
                })
            );

            return;
        }

        if (/^([0-9]|1[0-2])$/.test(value)) {
            setMonth(value);
        } else if (parseInt(value, 10) <= 12 && (value.length === 2 || value.substring(0, 1) === '0')) {
            setMonth(value);
        } else {
            setMonth('');
            value = '';
        }

        if (parseInt(value, 10) <= 12 && checkCurrentDate(value, year)) {
            setMonth(value);
        } else if (value.length === 1 && value.substring(0, 1) === '0') {
            setMonth(value);
        } else {
            setMonth('');
            value = '';
        }

        if (value) {
            dispatch(
                bindMeetFormData({
                    formdata: meetformData.formdata
                        ? meetformData.formdata.map(q => {
                            return {
                                ...q,
                                [sectionid]: {
                                    ...q[sectionid],
                                    [e.target.name]: value.length === 1 ? `0${value}` : value
                                }
                            };
                        })
                        : [ { [sectionid]: { [e.target.name]: value.length === 1 ? `0${value}` : value } } ]
                })
            );
        }
        if (value.length === 2 && yearRef.current) {
            yearRef.current.focus();
        }

    };

    const handleMonthBlur = e => {
        let value = e.target.value;

        if (value.length === 1) {
            value = `0${value}`;
        }
        setMonth(value);
        if ((rValue && !rValue.expiryYear && touchedfields.includes('expiryYear')) || !value) {
            setExpirationError(true);
            dispatch(bindPaymentError(true));
        } else {
            setExpirationError(false);
            if (!cvvError) {
                dispatch(bindPaymentError(false));
            }
        }

        if (!touchedfields.includes('expiryMonth') && !value) {
            dispatch(bindTouchedFields([ ...touchedfields, 'expiryMonth' ]));
        }
    };

    const handleYearChange = e => {
        const value = e.target.value;
        const yearValue = parseInt(value, 10);

        const currentYear = new Date().getFullYear() % 100;
        const maxYear = currentYear + 20;

        // Allow empty value for backspace/delete
        if (value === '') {
            setYear('');
            dispatch(
                bindMeetFormData({
                    formdata: meetformData.formdata
                        ? meetformData.formdata.map(q => {
                            return {
                                ...q,
                                [sectionid]: {
                                    ...q[sectionid],
                                    expiryYear: ''
                                }
                            };
                        })
                        : [ { [sectionid]: { expiryYear: '' } } ]
                })
            );

            return;
        }

        // const maxYearFirstNum = maxYear.toString().substring(0,1);
        if (/^\d{0,2}$/.test(value)) {

            if (value.length === 2 && yearValue >= currentYear && yearValue <= maxYear) {
                setYear(value);
            } else if (value.length < 2) {
                setYear(value);
            } else {
                setYear('');
            }

        }
        if (checkCurrentDate(month, value)) {
            setMonth(month);
        } else {
            setMonth('');
        }
        if (value) {
            dispatch(
                bindMeetFormData({
                    formdata: meetformData.formdata
                        ? meetformData.formdata.map(q => {
                            return {
                                ...q,
                                [sectionid]: {
                                    ...q[sectionid],
                                    [e.target.name]: e.target.value,
                                    'expiryMonth': rValue && !rValue.expiryMonth && month ? month : month
                                }
                            };
                        })
                        : [ { [sectionid]: { [e.target.name]: e.target.value } } ]
                })
            );
        }

    };

    const handleYearBlur = e => {
        if ((rValue && !rValue.expiryMonth && touchedfields.includes('expiryMonth')) || !e.target.value) {
            setExpirationError(true);
            dispatch(bindPaymentError(true));
        } else {
            const currentYear = new Date().getFullYear() % 100;
            const maxYear = currentYear + 20;
            const enteredYear = rValue && rValue.expiryYear ? parseInt(rValue.expiryYear, 10) : null;

            if (enteredYear !== null && (enteredYear < currentYear || enteredYear > maxYear)) {
                setYear('');
                dispatch(
                    bindMeetFormData({
                        formdata: meetformData.formdata
                            ? meetformData.formdata.map(q => {
                                return {
                                    ...q,
                                    [sectionid]: {
                                        ...q[sectionid],
                                        expiryYear: ''
                                    }
                                };
                            })
                            : [ { [sectionid]: { expiryYear: '' } } ]
                    })
                );
                setExpirationError(true);
                dispatch(bindPaymentError(true));
                if (!touchedfields.includes('expiryYear')) {
                    dispatch(bindTouchedFields([ ...touchedfields, 'expiryYear' ]));
                }
            } else {
                setExpirationError(false);
                if (!cvvError) {
                    dispatch(bindPaymentError(false));
                }
            }

        }

        if (!touchedfields.includes('expiryYear') && !e.target.value) {
            dispatch(bindTouchedFields([ ...touchedfields, 'expiryYear' ]));
        }
    };

    const st_1 = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start'
    };
    const st_2 = {
        display: 'flex',
        alignItems: 'center'
    };

    return (
        <>

            <div className = 'transaction_info_wrapper'>
                <div className = 'recurring_and_card_wrapper'>

                    <div
                        className = { `form_input_wrapper ${
                            (formSettings && formSettings.BillingInfoSettings
                             && formSettings.BillingInfoSettings.BillingInfo_Email.length <= 15)
                            || (meetuserData.email?.length <= 35) ? 'd-flex' : ''
                        } justify-content-between form-input-centered` }>
                        <label htmlFor = 'email'>
                            {formSettings && formSettings.BillingInfoSettings
                             && formSettings.BillingInfoSettings.BillingInfo_Email
                                ? formSettings.BillingInfoSettings.BillingInfo_Email
                                : t('clickandpledge.email')}
                            <span className = 'required-astrix'>*</span>
                        </label>
                        {meetuserData.email ? <p>{meetuserData.email}</p> : (
                            <div
                                className = {
                                    formSettings && formSettings.BillingInfoSettings
                                && formSettings.BillingInfoSettings.BillingInfo_Email.length <= 15
                                        ? 'w_half' : 'w_half'
                                } >
                                <input
                                    className = { `form-input required ${
                                        validationSec[sectionname]
                        && touchedfields.includes('email')
                        && rValue
                        && !rValue.email
                                            ? 'input-error'
                                            : ''
                                    }` }
                                    id = 'email'
                                    name = 'email'
                                    onBlur = { () => onBlurChange('email') }
                                    onChange = { e => {
                                        dispatch(
                                            bindMeetFormData({
                                                formdata: meetformData.formdata
                                                    ? meetformData.formdata.map(q => {
                                                        return {
                                                            ...q,
                                                            [sectionid]: {
                                                                ...q[sectionid],
                                                                [e.target.name]: e.target.value
                                                            }
                                                        };
                                                    })
                                                    : [ { [sectionid]: { [e.target.name]: e.target.value } } ]
                                            })
                                        );
                                    }
                                    }
                                    placeholder = { t('clickandpledge.email') }
                                    type = 'text'
                                    value = { rValue && rValue.email ? rValue.email : '' } />
                                {validationSec[sectionname]
                                    && touchedfields.includes('email')
                                    && paymentValidator.current.message(
                                        'email',
                                        rValue && rValue.email,
                                        'required|email',
                                        { className: 'text-danger' }
                                    )}
                            </div>
                        ) }
                    </div>

                    <div
                        className = { 'form_input_wrapper form_input_wrapper_custom  d-flex justify-content-between form-input-centered' }>
                        <div
                            className = {
                                formSettings && formSettings.BillingInfoSettings
                            && formSettings.BillingInfoSettings.BillingInfo_FirstName.length <= 8
                                    ? 'w_40' : ''
                            }>
                            <label htmlFor = 'first_name'>
                                {formSettings && formSettings.BillingInfoSettings
                                    ? formSettings.BillingInfoSettings.BillingInfo_FirstName
                                    : t('clickandpledge.firstName')}
                                <span className = 'required-astrix'>*</span>
                            </label>
                        </div>
                        <div
                            className = {
                                formSettings && formSettings.BillingInfoSettings
                            && formSettings.BillingInfoSettings.BillingInfo_FirstName.length <= 8
                                    ? 'w_60' : 'w_half'
                            }>
                            <input
                                className = { `form-input required ${
                                    validationSec[sectionname]
                                        && touchedfields.includes('first_name')
                                        && rValue
                                        && !rValue.first_name
                                        ? 'input-error'
                                        : ''
                                }` }
                                id = 'first_name'
                                maxLength = { 40 }
                                name = 'first_name'
                                onBlur = { () => onBlurChange('first_name') }
                                onChange = { e => {
                                    // let regex = /^[a-zA-Z0-9\s]*$/;
                                    const regex = /^[A-Za-z0-9.#&\-' ]+$/;

                                    if (e.target.value && !regex.test(e.target.value)) {
                                        return;
                                    }
                                    dispatch(
                                        bindMeetFormData({
                                            formdata: meetformData.formdata
                                                ? meetformData.formdata.map(q => {
                                                    return {
                                                        ...q,
                                                        [sectionid]: {
                                                            ...q[sectionid],
                                                            [e.target.name]: e.target.value
                                                        }
                                                    };
                                                })
                                                : [ { [sectionid]: { [e.target.name]: e.target.value } } ]
                                        })
                                    );
                                }
                                }
                                placeholder = { t('clickandpledge.firstName') }
                                ref = { firstName }
                                type = 'text'
                                value = { rValue && rValue.first_name ? rValue.first_name : '' } />
                            <span
                                className = { `error_icon ${
                                    validationSec[sectionname]
                                        && touchedfields.includes('first_name')
                                        && rValue
                                        && !rValue.first_name
                                        ? ''
                                        : 'd-none'
                                }` }
                                title = { t('clickandpledge.thisFieldIsRequired') } />
                            {validationSec[sectionname]
                                && touchedfields.includes('first_name')
                                && paymentValidator.current.message(
                                    'first_name',
                                    rValue && rValue.first_name,
                                    'required|min:2|max:40',
                                    { className: 'text-danger' }
                                )}


                        </div>

                    </div>

                    <div
                        className = { 'form_input_wrapper form_input_wrapper_custom  d-flex justify-content-between form-input-centered' }>
                        <div
                            className = {
                                formSettings && formSettings.BillingInfoSettings
                            && formSettings.BillingInfoSettings.BillingInfo_LastName.length <= 8
                                    ? 'w_40' : ''
                            }>
                            <label htmlFor = 'last_name'>
                                {formSettings && formSettings.BillingInfoSettings
                                    ? formSettings.BillingInfoSettings.BillingInfo_LastName
                                    : t('clickandpledge.lastName')}
                                <span className = 'required-astrix'>*</span>
                            </label>
                        </div>
                        <div
                            className = {
                                formSettings && formSettings.BillingInfoSettings
                            && formSettings.BillingInfoSettings.BillingInfo_LastName.length <= 8
                                    ? 'w_60' : 'w_half'
                            }>
                            <input
                                className = { `form-input required ${
                                    validationSec[sectionname]
                                        && touchedfields.includes('last_name')
                                        && rValue
                                        && !rValue.last_name
                                        ? 'input-error'
                                        : ''
                                }` }
                                id = 'last_name'
                                maxLength = { 50 }
                                name = 'last_name'
                                onBlur = { () => onBlurChange('last_name') }
                                onChange = { e => {
                                    // let regex = /^[a-zA-Z0-9\s]*$/;
                                    const regex = /^[A-Za-z0-9.#&\-' ]+$/;

                                    if (e.target.value && !regex.test(e.target.value)) {
                                        return;
                                    }
                                    dispatch(
                                        bindMeetFormData({
                                            formdata: meetformData.formdata
                                                ? meetformData.formdata.map(q => {
                                                    return {
                                                        ...q,
                                                        [sectionid]: {
                                                            ...q[sectionid],
                                                            [e.target.name]: e.target.value
                                                        }
                                                    };
                                                })
                                                : [ { [sectionid]: { [e.target.name]: e.target.value } } ]
                                        })
                                    );
                                }
                                }
                                placeholder = { t('clickandpledge.lastName') }
                                ref = { lastName }
                                type = 'text'
                                value = { rValue && rValue.last_name ? rValue.last_name : '' } />
                            <span
                                className = { `error_icon ${
                                    validationSec[sectionname]
                                        && touchedfields.includes('last_name')
                                        && rValue
                                        && !rValue.last_name
                                        ? ''
                                        : 'd-none'
                                }` }
                                title = { t('clickandpledge.thisFieldIsRequired') } />
                            {validationSec[sectionname]
                                && touchedfields.includes('last_name')
                                && paymentValidator.current.message(
                                    'last_name',
                                    rValue && rValue.last_name,
                                    'required|min:2|max:50',
                                    { className: 'text-danger' }
                                )}


                        </div>

                    </div>

                    <div
                        className = { 'form_input_wrapper form_input_wrapper_custom  d-flex justify-content-between form-input-centered' }>
                        <div
                            className = {
                                formSettings && formSettings.PaymentInfoLabelTextSettings
                            && formSettings.PaymentInfoLabelTextSettings.CreditCard_NameonCard.length <= 12
                                    ? 'w_40' : ''
                            }>
                            <label htmlFor = 'name_on_card'>
                                {formSettings && formSettings.PaymentInfoLabelTextSettings
                                    ? formSettings.PaymentInfoLabelTextSettings.CreditCard_NameonCard
                                    : t('clickandpledge.nameOnCard')}
                                <span className = 'required-astrix'>*</span>
                            </label>
                        </div>
                        <div
                            className = {
                                formSettings && formSettings.PaymentInfoLabelTextSettings
                            && formSettings.PaymentInfoLabelTextSettings.CreditCard_NameonCard.length <= 12
                                    ? 'w_60' : 'w_half'
                            }>
                            <input
                                className = { `form-input required ${
                                    validationSec[sectionname]
                                        && touchedfields.includes('noc')
                                        && rValue
                                        && !rValue.noc
                                        ? 'input-error'
                                        : ''
                                }` }
                                id = 'name_on_card'
                                maxLength = { 50 }
                                name = 'noc'
                                onBlur = { () => onBlurChange('noc') }
                                onChange = { e => {
                                    // let regex = /^[a-zA-Z0-9\s]*$/;
                                    const regex = /[\\"<&]/;

                                    if (e.target.value && regex.test(e.target.value)) {
                                        return;
                                    }
                                    dispatch(
                                        bindMeetFormData({
                                            formdata: meetformData.formdata
                                                ? meetformData.formdata.map(q => {
                                                    return {
                                                        ...q,
                                                        [sectionid]: {
                                                            ...q[sectionid],
                                                            [e.target.name]: e.target.value
                                                        }
                                                    };
                                                })
                                                : [ { [sectionid]: { [e.target.name]: e.target.value } } ]
                                        })
                                    );
                                }
                                }
                                placeholder = { t('clickandpledge.nameOnCard') }
                                ref = { noc }
                                type = 'text'
                                value = { rValue && rValue.noc ? rValue.noc : '' } />
                            <span
                                className = { `error_icon ${
                                    validationSec[sectionname]
                                        && touchedfields.includes('noc')
                                        && rValue
                                        && !rValue.noc
                                        ? ''
                                        : 'd-none'
                                }` }
                                title = { t('clickandpledge.thisFieldIsRequired') } />
                            {validationSec[sectionname]
                                && touchedfields.includes('noc')
                                && paymentValidator.current.message(
                                    'noc',
                                    rValue && rValue.noc,
                                    'required|noc_min|noc_max',
                                    { className: 'text-danger' }
                                )}

                            {/* {paymentValidator.current.fieldValid('noc') && nameError && <span className="text-danger">Enter your Last name</span>} */}
                        </div>

                    </div>


                    <div
                        className = { 'form_input_wrapper form_input_wrapper_custom  d-flex justify-content-between form-input-centered' }>
                        <div className = { '' }>
                            <label htmlFor = 'cc_number'>
                                {formSettings && formSettings.PaymentInfoLabelTextSettings
                                    ? formSettings.PaymentInfoLabelTextSettings.CreditCard_CardNumber
                                    : t('clickandpledge.cardNumber')}
                                <span className = 'required-astrix'>*</span>
                            </label>
                        </div>
                        <div className = 'w_half'>
                            <input
                                className = { `form-input required numeric-input CheckoutInput--cc ${
                                    validationSec[sectionname]
                                        && touchedfields.includes('ccn')
                                        && rValue
                                        && !rValue.ccn
                                        ? 'input-error'
                                        : ''
                                }` }
                                id = 'ccn'
                                maxLength = { 19 }
                                name = 'ccn'
                                onBlur = { () => onBlurChange('ccn') }
                                onChange = { e => {
                                    const value = e.target.value;
                                    const digits = value.replace(/\D/g, '');

                                    getCardType(digits);
                                    const isAmexCard = /^3[47]/.test(digits);
                                    const minLen = isAmexCard ? 4 : 3;

                                    if (rValue?.cvv) {
                                        if (rValue.cvv.length < minLen) {
                                            setCvvError(true);
                                            dispatch(bindPaymentError(true));
                                        } else {
                                            setCvvError(false);
                                            if (!expirationError) {
                                                dispatch(bindPaymentError(false));
                                            }
                                        }
                                    }

                                    dispatch(bindMeetFormData({
                                        formdata: meetformData.formdata
                                            ? meetformData.formdata.map(q => {
                                                return {
                                                    ...q,
                                                    [sectionid]: {
                                                        ...q[sectionid],
                                                        [e.target.name]: digits
                                                    }
                                                };
                                            })
                                            : [ { [sectionid]: { [e.target.name]: digits } } ]
                                    })
                                    );
                                } }

                                placeholder = { t('clickandpledge.cardNumberPlaceholder') }
                                ref = { ccn }
                                type = 'text'
                                value = {
                                    rValue && rValue.ccn
                                        ? typeof rValue.ccn === 'string'
                                            ? rValue.ccn.match(/.{1,4}/g)?.join(' ') || rValue.ccn
                                            : rValue.ccn
                                        : ''
                                } />

                            <span
                                className = { `error_icon ${
                                    validationSec[sectionname]
                                        && touchedfields.includes('ccn')
                                        && rValue
                                        && !rValue.ccn
                                        ? ''
                                        : 'd-none'
                                }` }
                                title = { t('clickandpledge.thisFieldIsRequired') } />
                            {validationSec[sectionname]
                                && touchedfields.includes('ccn')
                                && paymentValidator.current.message(
                                    'ccn',
                                    rValue && rValue.ccn,
                                    'required|card_num|ccn_min|ccn_max',
                                    { className: 'text-danger' }
                                )}

                        </div>

                        <div className = 'cc_icons'>
                            <span
                                className = {
                                    rValue && rValue.ccn && cardType === 'CC_visa' ? '' : 'd-none'
                                }>
                                <img
                                    alt = 'visa'
                                    className = 'visa'
                                    src = 'images/clickandpledge/visa.svg' />
                            </span>
                            <span
                                className = {
                                    rValue && rValue.ccn && cardType === 'CC_mastercard' ? '' : 'd-none'
                                }>
                                <img
                                    alt = 'mastercard'
                                    className = 'mastercard'
                                    src = 'images/clickandpledge/mastercard.svg' />
                            </span>
                            <span
                                className = {
                                    rValue && rValue.ccn && cardType === 'CC_americanexpress' ? '' : 'd-none'
                                }>
                                <img
                                    alt = 'amex'
                                    className = 'amex'
                                    src = 'images/clickandpledge/amex.svg' />
                            </span>
                            <span
                                className = {
                                    rValue && rValue.ccn && cardType === 'CC_discover' ? '' : 'd-none'
                                }>
                                <img
                                    alt = 'discover'
                                    className = 'discover'
                                    src = 'images/clickandpledge/discover.svg' />
                            </span>
                            <span
                                className = {
                                    rValue && rValue.ccn && cardType === 'CC_jcb' ? '' : 'd-none'
                                }>
                                <img
                                    alt = 'jcb'
                                    className = 'jcb'
                                    src = 'images/clickandpledge/jcb.svg' />
                            </span>
                            <div
                                className = { `fading-icons fading ${rValue && rValue.ccn ? 'd-none' : ''}` }
                                ref = { fadingRef }>
                                {(!cardType || cardType === 'CC_visa' || paymentinfo.CC_visa) && (
                                    <span>
                                        <img
                                            alt = 'visa'
                                            className = 'visa'
                                            src = 'images/clickandpledge/visa.svg' />
                                    </span>
                                )}
                                {(!cardType || cardType === 'CC_mastercard' || paymentinfo.CC_mastercard) && (
                                    <span>
                                        <img
                                            alt = 'MasterCard'
                                            className = 'visa'
                                            src = 'images/clickandpledge/mastercard.svg' />
                                    </span>
                                )}
                                {(cardType === 'CC_americanexpress' || paymentinfo.CC_americanexpress) && (
                                    <span>
                                        <img
                                            alt = 'AmericanExpress'
                                            className = 'visa'
                                            src = 'images/clickandpledge/amex.svg' />
                                    </span>
                                )}
                                {(cardType === 'CC_discover' || paymentinfo.CC_discover) && (
                                    <span>
                                        <img
                                            alt = 'Discover'
                                            className = 'dinersclub'
                                            src = 'images/clickandpledge/discover.svg' />
                                    </span>
                                )}
                                {(cardType === 'CC_jcb' || paymentinfo.CC_jcb) && (
                                    <span>
                                        <img
                                            alt = 'jcb'
                                            className = 'jcb'
                                            src = 'images/clickandpledge/jcb.svg' />
                                    </span>
                                )}

                            </div>
                        </div>

                    </div>

                    <div
                        className = 'form_input_wrapper from_input_with_error form_input_wrapper_custom  d-flex justify-content-between form-input-centered'>
                        <div className = 'form_input_field'>
                            <div className = 'form_input_field_label'>
                                <label htmlFor = 'name_on_card'>
                                    {formSettings && formSettings.PaymentInfoLabelTextSettings
                                 && formSettings.PaymentInfoLabelTextSettings.CreditCard_ExpirationDate
                                        ? formSettings.PaymentInfoLabelTextSettings.CreditCard_ExpirationDate
                                        : t('clickandpledge.expires')}
                                    <span className = 'required-astrix'>*</span>
                                </label>
                            </div>
                            <div
                                className = 'card-expiry-container w_half'
                                style = { st_1 }>
                                <div
                                    style = { st_2 }>
                                    <input
                                        aria-label = { t('clickandpledge.expiryMonth') }
                                        className = { `expiry-input ${
                                            touchedfields.includes('expiryMonth') && !month ? 'input-error' : ''
                                        }` }
                                        maxLength = '2'
                                        name = 'expiryMonth'
                                        onBlur = { handleMonthBlur }
                                        onChange = { handleMonthChange }
                                        placeholder = { t('clickandpledge.expiryMonthPlaceholder') }
                                        ref = { expMonth }
                                        type = 'text'
                                        value = {
                                            rValue && !month && rValue.expiryMonth ? rValue.expiryMonth : month
                                        } />
                                    <span className = 'divider'>/</span>
                                    <input
                                        aria-label = { t('clickandpledge.expiryYear') }
                                        className = { `expiry-input ${
                                            touchedfields.includes('expiryYear') && !year ? 'input-error' : ''
                                        }` }
                                        maxLength = '2'
                                        name = 'expiryYear'
                                        onBlur = { handleYearBlur }
                                        onChange = { handleYearChange }
                                        placeholder = { t('clickandpledge.expiryYearPlaceholder') }
                                        ref = { yearRef }
                                        type = 'text'
                                        value = {
                                            rValue && !year && rValue.expiryYear ? rValue.expiryYear : year
                                        } />
                                </div>
                                <div className = 'inline-err-msg'>
                                    { (() => {
                                        const monthMissing = touchedfields.includes('expiryMonth') && !month;
                                        const yearMissing = touchedfields.includes('expiryYear') && !year;

                                        if (monthMissing || yearMissing) {
                                            return (
                                                <span className = 'expiry-error-msg'>
                                                    { t('clickandpledge.required') }
                                                </span>
                                            );
                                        }
                                        if (expirationError) {
                                            return (
                                                <span className = 'expiry-error-msg'>
                                                    { t('clickandpledge.invalidExpirationDate') }
                                                </span>
                                            );
                                        }

                                        return null;
                                    })() }
                                </div>
                            </div>
                        </div>

                    </div>

                    <div
                        className = 'form_input_wrapper card_info_details  d-flex inline_card_info card-info-container'>
                        <div className = 'card-info-row'>
                            <div className = 'cvv_label'>
                                <label
                                    className = 'cvv-label'
                                    htmlFor = 'card_cvv' >
                                    {formSettings && formSettings.PaymentInfoLabelTextSettings
                                     && formSettings.PaymentInfoLabelTextSettings.CreditCard_Verification
                                        ? formSettings.PaymentInfoLabelTextSettings.CreditCard_Verification
                                        : t('clickandpledge.cvv')}
                                    <span className = 'required-astrix'>*</span>
                                </label>
                            </div>
                            <div>
                                <div
                                    className = ''
                                    style = { st_1 }>
                                    <input
                                        aria-autocomplete = 'list'
                                        autoComplete = 'off'
                                        className = { `form-input required numeric-input CheckoutInput--cc ${
                                            touchedfields.includes('cvv')
                                            && (validationSec[sectionname] || isPaySecTouched || cvvError)
                                            && (!rValue?.cvv || cvvError || isCvvInvalid)
                                                ? 'input-error'
                                                : ''
                                        }` }
                                        id = 'card_cvv'
                                        maxLength = { 4 }

                                        name = { 'cvv' }
                                        onBlur = { () => onBlurChange('cvv') }
                                        onChange = { e => {
                                            const format = /^\d+$/;

                                            if (e.target.value && !format.test(e.target.value)) {
                                                return;
                                            }
                                            const val = e.target.value;
                                            const name = e.target.name.split('_')[0];

                                            const digits = String(rValue?.ccn || '').replace(/\D/g, '');
                                            const isAmexCard = cardType === 'CC_americanexpress' || /^3[47]/.test(digits);
                                            const minLen = isAmexCard ? 4 : 3;

                                            if (val && val.length < minLen) {
                                                setCvvError(true);
                                                dispatch(bindPaymentError(true));
                                            } else {
                                                setCvvError(false);
                                                if (!expirationError) {
                                                    dispatch(bindPaymentError(false));
                                                }
                                            }

                                            dispatch(
                                            bindMeetFormData({
                                                formdata: meetformData.formdata
                                                    ? meetformData.formdata.map(q => {
                                                        return {
                                                            ...q,
                                                            [sectionid]: {
                                                                ...q[sectionid],
                                                                [name]: val
                                                            }
                                                        };
                                                    })
                                                    : [ { [sectionid]: { [name]: val } } ]
                                            })
                                            );
                                        }
                                        }
                                        onFocus = { handleFocus }
                                        onTouchStart = { handleFocus } // for ios
                                        placeholder = { t('clickandpledge.cvv') }
                                        readOnly = { isReadOnly }
                                        ref = { cvv }
                                        type = 'text'
                                        value = { rValue && rValue.cvv ? rValue.cvv : '' } />
                                    <div className = 'inline-err-msg'>
                                        {paymentValidator?.current?.message(
                                            'cvv',
                                            rValue && rValue.cvv,
                                            'required|cvv_check',
                                            {
                                                messages: {
                                                    required: t('clickandpledge.thisFieldIsRequired') || 'Required',
                                                    cvv_check: t('clickandpledge.invalidCvv') || 'Invalid CVV'
                                                },
                                                className: 'text-danger'
                                            }
                                        )}
                                        {touchedfields.includes('cvv')
                                            && (validationSec[sectionname] || isPaySecTouched || cvvError)
                                            && !paymentValidator?.current?.message(
                                                'cvv',
                                                rValue && rValue.cvv,
                                                'required|cvv_check'
                                            )
                                            && (
                                                <span className = 'text-danger'>
                                                    { rValue?.cvv
                                                        ? isCvvInvalid || cvvError
                                                            ? t('clickandpledge.invalidCvv') || 'Invalid CVV'
                                                            : null
                                                        : t('clickandpledge.thisFieldIsRequired') || 'Required' }
                                                </span>
                                            )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className = 'card-info-row'>
                            <div className = 'zip_label'>
                                <label htmlFor = 'card_zip' >
                                    {formSettings && formSettings.BillingInfoSettings
                                     && formSettings.BillingInfoSettings.BillingInfo_ZipPostalCode
                                        ? formSettings.BillingInfoSettings.BillingInfo_ZipPostalCode
                                        : t('clickandpledge.zip')}
                                    <span className = 'required-astrix'>*</span>
                                </label>
                            </div>

                            <div className = 'zip_input'>
                                <input
                                    aria-autocomplete = 'list'
                                    autoComplete = 'off'
                                    className = { `form-input required numeric-input ${
                                        validationSec[sectionname]
                                            && touchedfields.includes('zipcode')
                                            && rValue
                                            && !rValue.zipcode
                                            ? 'input-error'
                                            : ''
                                    }` }
                                    id = 'zipcode'
                                    maxLength = { 20 }
                                    name = 'zipcode'
                                    onBlur = { () => onBlurChange('zipcode') }
                                    onChange = { e =>
                                        dispatch(
                                            bindMeetFormData({
                                                formdata: meetformData.formdata
                                                    ? meetformData.formdata.map(q => {
                                                        return {
                                                            ...q,
                                                            [sectionid]: {
                                                                ...q[sectionid],
                                                                [e.target.name]: e.target.value
                                                            }
                                                        };
                                                    })
                                                    : [ { [sectionid]: { [e.target.name]: e.target.value } } ]
                                            })
                                        )
                                    }
                                    onFocus = { handleZipFocus }
                                    onTouchStart = { handleZipFocus } // for ios
                                    placeholder = { t('clickandpledge.zipPlaceholder') }
                                    readOnly = { isZipReadOnly }
                                    ref = { zip }
                                    type = 'text'
                                    value = { rValue && rValue.zipcode ? rValue.zipcode : '' } />
                            </div>
                        </div>
                    </div>
                    <span className = 'inline-err-msg'>
                        {validationSec[sectionname]
                            && touchedfields.includes('zipcode')
                            && paymentValidator.current.message(
                                'zipcode',
                                rValue && rValue.zipcode,
                                'required|zip_check',
                                { className: 'text-danger' }
                            )}

                    </span>


                </div>
            </div>

        </>
    );
}

export default translate(PaymentSection);
